import React, { useCallback } from "react"
import PropTypes from "prop-types"
import { Button, DatePicker, Flex, Input, Popover, Space, Typography } from "antd"
import dayjs from "dayjs"
import styles from "./DetailsToolbar.module.css"
import { CloseOutlined, FilterOutlined, SearchOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import DateSwitcher from "modules/datepicker/DateSwitcher"
import { DETAILS_RANGE_OPTIONS } from "constants/accounting"
import { DATE_FORMAT } from "modules/datepicker/constants"
import MultiSelect from "components/common/MultiSelect"

export default function DetailsToolbar({
  currentDate,
  currentRange,
  isSales,
  searchTerm,
  onSearchChange,
  filterVisible,
  activeFilterCount,
  draftFilters,
  setDraftFilters,
  paymentTypeOptions,
  bookingTypeOptions,
  transactionTypeOptions,
  experienceOptions,
  categoryOptions,
  onFilterOpenChange,
  onApplyFilters,
  onCancelFilters,
  onResetFilters,
  onRangeChange,
  onChangeFilters,
  dateGteq,
  dateLteq,
  onCustomDateChange,
  hideFilters,
  hideDateSwitcher,
  hideRangeTabs,
  leftAlignDate,
  rightSlot
}) {
  const { t } = useTranslation()

  const handleSearchChange = useCallback((e) => onSearchChange(e.target.value), [onSearchChange])

  const getDateSwitcherType = () => {
    if (currentRange === "day") return "date"
    if (currentRange === "week") return "week"
    return "month"
  }


  const handleDateChange = useCallback(
    (e) => {
      onChangeFilters?.({ target: { name: "date", value: e.target.value } })
    },
    [onChangeFilters]
  )

  return (
    <Flex justify="space-between" align="center" gap={10} wrap className="w-full">
      {!hideDateSwitcher && (
        <Flex align="center" gap={10} className={`${styles.dateRow} ${leftAlignDate ? "" : "ml-auto"}`}>
          {currentRange === "custom" ? (
            <DatePicker.RangePicker
              value={
                dateGteq && dateLteq
                  ? [dayjs(dateGteq, DATE_FORMAT), dayjs(dateLteq, DATE_FORMAT)]
                  : null
              }
              onChange={(dates) => {
                if (dates?.length === 2) {
                  onCustomDateChange?.({
                    gteq: dates[0].format(DATE_FORMAT),
                    lteq: dates[1].format(DATE_FORMAT)
                  })
                } else {
                  onCustomDateChange?.({
                    gteq: dayjs().startOf("month").format(DATE_FORMAT),
                    lteq: dayjs().endOf("month").format(DATE_FORMAT)
                  })
                }
              }}
              format={DATE_FORMAT}
              className={styles.rangePicker}
            />
          ) : (
            <DateSwitcher
              value={currentDate}
              type={getDateSwitcherType()}
              onChange={handleDateChange}
              name="date"
            />
          )}
          {!hideRangeTabs && (
            <div className={styles.rangeTab}>
              {DETAILS_RANGE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`${styles.rangeTabItem} ${currentRange === opt.value ? styles.rangeTabActive : ""}`}
                  onClick={() => onRangeChange(opt.value)}
                >
                  {t(`accounting.details_range.${opt.value}`)}
                </button>
              ))}
            </div>
          )}
        </Flex>
      )}
      {rightSlot && <Flex align="center" gap={8}>{rightSlot}</Flex>}
      {!hideFilters && <Space className="ml-auto">
        <Input
          prefix={<SearchOutlined />}
          placeholder={isSales ? t("accounting.details.search.sale") : t("accounting.details.search.expense")}
          value={searchTerm}
          onChange={handleSearchChange}
          allowClear
          className={styles.searchInput}
        />
        {activeFilterCount > 0 && (
          <Button color="primary" variant="outlined" icon={<CloseOutlined />} onClick={onResetFilters}>
            {t("accounting.details.filters.clear")}
          </Button>
        )}
        <Popover
          open={filterVisible}
          onOpenChange={onFilterOpenChange}
          trigger="click"
          placement="bottomRight"
          title={t("accounting.details.filters.title")}
          styles={{ body: { width: 350, maxHeight: "70vh", overflowY: "auto" } }}
          content={
            <Flex vertical gap="middle">
              {isSales ? (
                <>
                  <Flex vertical gap="small">
                    <Typography.Text strong>{t("accounting.details.filters.payment_type")}</Typography.Text>
                    <MultiSelect
                      style={{ width: "100%", minWidth: "unset", maxWidth: "unset" }}
                      placeholder={t("accounting.details.filters.payment_type")}
                      options={paymentTypeOptions}
                      value={draftFilters.payment_type || []}
                      onChange={(value) => setDraftFilters((prev) => ({ ...prev, payment_type: value }))}
                    />
                  </Flex>
                  <Flex vertical gap="small">
                    <Typography.Text strong>{t("accounting.details.filters.booking_type")}</Typography.Text>
                    <MultiSelect
                      style={{ width: "100%", minWidth: "unset", maxWidth: "unset" }}
                      placeholder={t("accounting.details.filters.booking_type")}
                      options={bookingTypeOptions}
                      value={draftFilters.booking_type || []}
                      onChange={(value) => setDraftFilters((prev) => ({ ...prev, booking_type: value }))}
                    />
                  </Flex>
                  <Flex vertical gap="small">
                    <Typography.Text strong>{t("accounting.details.filters.experience")}</Typography.Text>
                    <MultiSelect
                      style={{ width: "100%", minWidth: "unset", maxWidth: "unset" }}
                      placeholder={t("accounting.details.filters.experience")}
                      options={experienceOptions}
                      value={draftFilters.experience || []}
                      onChange={(value) => setDraftFilters((prev) => ({ ...prev, experience: value }))}
                    />
                  </Flex>
                  <Flex vertical gap="small">
                    <Typography.Text strong>{t("accounting.details.filters.transaction_type")}</Typography.Text>
                    <MultiSelect
                      style={{ width: "100%", minWidth: "unset", maxWidth: "unset" }}
                      placeholder={t("accounting.details.filters.transaction_type")}
                      options={transactionTypeOptions}
                      value={draftFilters.transaction_type || []}
                      onChange={(value) => setDraftFilters((prev) => ({ ...prev, transaction_type: value }))}
                    />
                  </Flex>
                </>
              ) : (
                <Flex vertical gap="small">
                  <Typography.Text strong>{t("accounting.details.filters.category")}</Typography.Text>
                  <MultiSelect
                    style={{ width: "100%", minWidth: "unset", maxWidth: "unset" }}
                    placeholder={t("accounting.details.filters.category")}
                    options={categoryOptions}
                    value={draftFilters.category_id || []}
                    onChange={(value) => setDraftFilters((prev) => ({ ...prev, category_id: value }))}
                  />
                </Flex>
              )}
              <Flex justify="space-between">
                <Button onClick={onCancelFilters}>{t("global.cancel")}</Button>
                <Button type="primary" onClick={onApplyFilters}>
                  {t("accounting.details.filters.apply")}
                </Button>
              </Flex>
            </Flex>
          }
        >
          <Button icon={<FilterOutlined />}>
            {activeFilterCount > 0
              ? t("accounting.details.filters.filter_count", { count: activeFilterCount })
              : t("accounting.details.filters.title")}
          </Button>
        </Popover>
      </Space>}
    </Flex>
  )
}

DetailsToolbar.propTypes = {
  currentDate: PropTypes.string,
  currentRange: PropTypes.string,
  isSales: PropTypes.bool,
  searchTerm: PropTypes.string,
  onSearchChange: PropTypes.func.isRequired,
  filterVisible: PropTypes.bool,
  activeFilterCount: PropTypes.number,
  draftFilters: PropTypes.object,
  setDraftFilters: PropTypes.func.isRequired,
  paymentTypeOptions: PropTypes.array,
  bookingTypeOptions: PropTypes.array,
  transactionTypeOptions: PropTypes.array,
  experienceOptions: PropTypes.array,
  categoryOptions: PropTypes.array,
  onFilterOpenChange: PropTypes.func.isRequired,
  onApplyFilters: PropTypes.func.isRequired,
  onCancelFilters: PropTypes.func.isRequired,
  onResetFilters: PropTypes.func.isRequired,
  onChangeFilters: PropTypes.func.isRequired,
  onRangeChange: PropTypes.func.isRequired,
  dateGteq: PropTypes.string,
  dateLteq: PropTypes.string,
  onCustomDateChange: PropTypes.func,
  hideFilters: PropTypes.bool,
  hideDateSwitcher: PropTypes.bool,
  hideRangeTabs: PropTypes.bool,
  leftAlignDate: PropTypes.bool,
  rightSlot: PropTypes.node
}
